import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ErrorState,
  SearchField,
  SegmentedControl,
  StatusPill,
  TableCard,
  TableEmptyRow,
  TableSkeletonRows,
  Toolbar,
} from '../../components/ui';
import type { StatusTone } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { MISSING, formatDate, formatNumber } from '../../utils/format';
import type { PreviewRow, RowTone } from './csvPreview';

export type RowFilter = 'all' | 'attention';

const COLUMN_COUNT = 9;
const PAGE_SIZES = [25, 50, 100];

const TONE: Record<RowTone, { tone: StatusTone; label: string }> = {
  ready: { tone: 'success', label: 'Ready' },
  review: { tone: 'warning', label: 'Needs review' },
  error: { tone: 'danger', label: 'Fix needed' },
};

const matches = (row: PreviewRow, query: string) =>
  [row.name, row.email, row.role, row.department].some((value) => value.toLowerCase().includes(query));

const CUSTOM_ONLY_HINT = 'Used only if this employee is already on a custom allotment. Otherwise the tenure policy sets it.';
const POLICY_HINT = 'Set by the tenure policy from startDate. Employees already on a custom allotment keep their hours.';

/**
 * An hours cell. Custom rows show the CSV value. Other rows show it too when
 * one was typed (the server writes it for employees already on a custom
 * allotment), muted and explained; a blank cell reads "Policy".
 */
const HoursCell: React.FC<{ row: PreviewRow; value: number | null }> = ({ row, value }) => {
  if (row.customAllotment) return <TableCell align="right">{formatNumber(value)}</TableCell>;
  const hint = value === null ? POLICY_HINT : CUSTOM_ONLY_HINT;
  return (
    <TableCell align="right">
      <Tooltip title={hint} describeChild>
        <Box
          component="span"
          sx={{
            color: sbg.text.secondary,
            cursor: 'help',
            textDecoration: 'underline dotted',
            textDecorationColor: sbg.border.strong,
            textUnderlineOffset: '3px',
          }}
        >
          {value === null ? 'Policy' : formatNumber(value)}
          {value !== null && <span className="sbg-sr-only"> (used only if already on a custom allotment)</span>}
        </Box>
      </Tooltip>
    </TableCell>
  );
};

const PreviewTableRow: React.FC<{ row: PreviewRow }> = ({ row }) => {
  const status = TONE[row.tone];
  return (
    <TableRow sx={{ verticalAlign: 'top' }}>
      <TableCell align="right" sx={{ color: sbg.text.secondary, width: 56 }}>
        {formatNumber(row.rowNumber)}
      </TableCell>
      <TableCell component="th" scope="row" sx={{ minWidth: 200, maxWidth: 280 }}>
        <Typography variant="labelMd" component="div" noWrap title={row.name || undefined} sx={{ color: sbg.text.primary }}>
          {row.name || MISSING}
        </Typography>
        <Typography variant="body2" component="div" noWrap title={row.email || undefined} sx={{ color: sbg.text.secondary }}>
          {row.email || MISSING}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: 240, maxWidth: 380 }}>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        {row.issues.length > 0 && (
          <Box component="ul" sx={{ m: 0, mt: 0.75, p: 0, listStyle: 'none', display: 'grid', gap: 0.5 }}>
            {row.issues.map((issue, index) => (
              <Typography key={index} component="li" variant="body2" sx={{ color: sbg.text.secondary, whiteSpace: 'normal' }}>
                {issue.message}
              </Typography>
            ))}
          </Box>
        )}
        {row.note && (
          <Typography variant="body2" component="p" sx={{ mt: 0.75, color: sbg.text.secondary, whiteSpace: 'normal' }}>
            {row.note}
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ minWidth: 120 }}>{row.department || MISSING}</TableCell>
      <TableCell sx={{ minWidth: 120 }}>{row.role || MISSING}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {row.startDate ? formatDate(row.startDate) : row.startDateRaw || MISSING}
      </TableCell>
      <HoursCell row={row} value={row.pto} />
      <HoursCell row={row} value={row.sick} />
      <TableCell align="right">{formatNumber(row.wfh)}</TableCell>
    </TableRow>
  );
};

export interface PreviewTableProps {
  fileName: string;
  status: 'parsing' | 'ready' | 'error';
  rows: PreviewRow[];
  /** False when the file is blank (not even a header row). */
  hasHeader: boolean;
  attentionCount: number;
  filter: RowFilter;
  onFilterChange: (filter: RowFilter) => void;
  /** Opens the file picker: a File the browser can't read can only be chosen again. */
  onChooseAnother: () => void;
  /** Drop the table's own border and radius when it sits full-bleed inside a card. */
  embedded?: boolean;
}

/**
 * The parsed preview (book §11.10): one row per CSV row with a status pill and,
 * when something is wrong, what and how to fix it. Search, the attention filter
 * and paging are view-only; the upload always sends the whole file.
 */
const PreviewTable: React.FC<PreviewTableProps> = ({
  fileName,
  status,
  rows,
  hasHeader,
  attentionCount,
  filter,
  onFilterChange,
  onChooseAnother,
  embedded = false,
}) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZES[0]);
  const normalizedQuery = query.trim().toLowerCase();

  useEffect(() => {
    setPage(0);
  }, [filter, normalizedQuery, rows]);

  const visible = useMemo(
    () =>
      rows.filter(
        (row) => (filter === 'all' || row.tone !== 'ready') && (!normalizedQuery || matches(row, normalizedQuery)),
      ),
    [rows, filter, normalizedQuery],
  );
  const pageRows = visible.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const hasRows = status === 'ready' && rows.length > 0;

  return (
    <TableCard
      aria-label={`Rows in ${fileName}`}
      maxHeight={600}
      sx={embedded ? { border: 0, borderRadius: 0 } : undefined}
      toolbar={
        hasRows ? (
          <Toolbar
            end={
              <SearchField
                value={query}
                onChange={setQuery}
                label="Search rows"
                placeholder="Search name, email, role or department"
                width={320}
              />
            }
          >
            <SegmentedControl<RowFilter>
              aria-label="Rows to show"
              size="small"
              value={filter}
              onChange={onFilterChange}
              options={[
                { value: 'all', label: `All rows (${formatNumber(rows.length)})` },
                { value: 'attention', label: `Needs attention (${formatNumber(attentionCount)})` },
              ]}
            />
          </Toolbar>
        ) : undefined
      }
      footer={
        visible.length > PAGE_SIZES[0] ? (
          <TablePagination
            component="div"
            count={visible.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={PAGE_SIZES}
            labelRowsPerPage="Rows per page"
          />
        ) : undefined
      }
    >
      <Table stickyHeader aria-label={`Preview of ${fileName}`}>
        <TableHead>
          <TableRow>
            <TableCell align="right">Row</TableCell>
            <TableCell>Employee</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Department</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Start date</TableCell>
            <TableCell align="right">PTO (h)</TableCell>
            <TableCell align="right">Sick (h)</TableCell>
            <TableCell align="right">WFH (days)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {status === 'parsing' && <TableSkeletonRows columns={COLUMN_COUNT} />}

          {status === 'error' && (
            <TableRow sx={{ '&&:hover': { bgcolor: 'transparent' } }}>
              <TableCell colSpan={COLUMN_COUNT} sx={{ borderBottom: 0 }}>
                <ErrorState
                  compact
                  title="Couldn’t read this file"
                  body="Your browser couldn’t open it, which usually means the file was changed, moved or deleted after you chose it. Choose it again to check the latest version."
                  onRetry={onChooseAnother}
                  retryLabel="Choose file again"
                />
              </TableCell>
            </TableRow>
          )}

          {status === 'ready' && rows.length === 0 && (
            <TableEmptyRow
              colSpan={COLUMN_COUNT}
              title={hasHeader ? 'No employee rows in this file' : 'This file is empty'}
              body={
                hasHeader
                  ? 'It has a header row but nothing under it. Add one row per employee, then choose the file again.'
                  : 'Download the template, add one row per employee, then choose the file.'
              }
              action={
                <Button variant="outlined" size="small" onClick={onChooseAnother}>
                  Choose another file
                </Button>
              }
            />
          )}

          {hasRows && visible.length === 0 &&
            (normalizedQuery ? (
              <TableEmptyRow
                colSpan={COLUMN_COUNT}
                title={`No rows match “${query.trim()}”`}
                body={
                  filter === 'attention'
                    ? 'Only rows that need attention are shown. Clear the search, or show all rows.'
                    : 'Search looks at name, email, role and department.'
                }
                action={
                  <Button variant="outlined" size="small" onClick={() => setQuery('')}>
                    Clear search
                  </Button>
                }
              />
            ) : (
              <TableEmptyRow
                colSpan={COLUMN_COUNT}
                title="No rows need attention"
                body="Every row passed the checks."
                action={
                  <Button variant="outlined" size="small" onClick={() => onFilterChange('all')}>
                    Show all rows
                  </Button>
                }
              />
            ))}

          {hasRows && pageRows.map((row) => <PreviewTableRow key={row.rowNumber} row={row} />)}
        </TableBody>
      </Table>
    </TableCard>
  );
};

export default PreviewTable;
